import React from 'react';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { Body5, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { formatKeyName } from './templateEditorUtils';
import { InputFieldWrapper, SaveButtonWrapper, TextareaField } from './templateActionStyles';

function EditTemplateInputs({ template, setTemplate }) {
  const [inputKeys, setInputKeys] = React.useState([]);

  React.useEffect(() => {
    const omittedKeys = ['id', 'template_type', 'is_editable', 'runnable', 'user_supplied_template_doc'];
    const keys = Object.keys(template || {}).filter((key) => !omittedKeys.includes(key));
    setInputKeys(keys);
  }, [template]);

  const handleInputChange = (key, value) => {
    setTemplate((currentTemplate) => ({
      ...currentTemplate,
      [key]: value,
    }));
  };

  return (
    <Container>
      {inputKeys.map((key) => (
        <InputFieldWrapper key={key}>
          <Container bottom={8} left={1}>
            <Body5 bold>{formatKeyName(key)}</Body5>
          </Container>
          <TextareaField value={template[key] || ''} onChange={(e) => handleInputChange(key, e.target.value)} />
        </InputFieldWrapper>
      ))}
    </Container>
  );
}

export function TemplateMetadataEditor({ template, onTemplateChange, onCancel }) {
  const [editableTemplate, setEditableTemplate] = React.useState(template);
  const [formError, setFormError] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);

  React.useEffect(() => {
    setEditableTemplate(template);
    setFormError(null);
    setMessage(null);
  }, [template]);

  const handleSaveTemplate = async () => {
    setIsSubmittingToApi(true);
    setFormError(null);
    setMessage(null);

    const url = `v1/genai/templates/${template.id}`;
    const { status, data, error } = await sendApiRequest('patch', url, editableTemplate);
    handle400Statuses(status);
    logNetRequest(url, status, data, error);
    setIsSubmittingToApi(false);

    if (status === 200) {
      onTemplateChange(data);
      setMessage('Template saved successfully');
      return;
    }

    if (status === 403) {
      setFormError('You are not authorized to edit this template');
      return;
    }

    setFormError(error || 'Something went wrong, try again');
  };

  return (
    <Container top={0}>
      <EditTemplateInputs template={editableTemplate} setTemplate={setEditableTemplate} />
      {message && (
        <Container top={16}>
          <Body5>{message}</Body5>
        </Container>
      )}
      {formError && (
        <Container top={16}>
          <ErrorText>{formError}</ErrorText>
        </Container>
      )}
      <Container top={24} row style={{ gap: '16px', width: '280px' }}>
        <SaveButtonWrapper>
          <FlatButton disabled={isSubmittingToApi} onClick={handleSaveTemplate}>
            {isSubmittingToApi ? 'Saving...' : 'Save'}
          </FlatButton>
        </SaveButtonWrapper>
        {!isSubmittingToApi && <TransparentFlatButton onClick={onCancel}>Back To Actions</TransparentFlatButton>}
      </Container>
    </Container>
  );
}
